import { useLayoutEffect, useRef } from 'react';
import type { CaptureMode } from '../state/store';

interface Props {
  mode: CaptureMode;
  disabled?: boolean;
  onChange(mode: CaptureMode): void;
  /** When provided, restricts the visible modes to this subset. */
  allowed?: readonly CaptureMode[];
}

const MODES: { id: CaptureMode; label: string }[] = [
  { id: 'single', label: 'Single' },
  { id: 'strip3', label: '3-Strip' },
  { id: 'strip4', label: '4-Strip' },
  { id: 'video', label: 'Video' },
  { id: 'boomerang', label: 'Boomerang' },
];

/**
 * iOS Camera-style snap-scrolling mode picker. All modes live in a single
 * horizontal track; the selected one auto-centers and is highlighted, the
 * neighbors fade off to either side. Swipe / drag horizontally to change.
 * Tapping a non-active mode smooth-scrolls it into the center.
 */
export function ModePicker({ mode, disabled, onChange, allowed }: Props) {
  const visible = allowed
    ? MODES.filter((m) => allowed.includes(m.id))
    : MODES;

  const railRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Map<CaptureMode, HTMLButtonElement>>(new Map());
  // Tracks which mode we last reported up so we don't fire onChange in a
  // tight scroll loop. Also acts as a guard against the layout effect's
  // smooth-scroll triggering its own scroll-detected onChange.
  const lastEmittedRef = useRef<CaptureMode>(mode);
  const hasMountedRef = useRef(false);

  // External mode changes → smooth-scroll the selected chip into center.
  useLayoutEffect(() => {
    const rail = railRef.current;
    const node = itemRefs.current.get(mode);
    if (!rail || !node) return;
    const target =
      node.offsetLeft + node.offsetWidth / 2 - rail.clientWidth / 2;
    lastEmittedRef.current = mode;
    rail.scrollTo({
      left: target,
      behavior: hasMountedRef.current ? 'smooth' : 'auto',
    });
    hasMountedRef.current = true;
  }, [mode]);

  // Detect which chip is centered as the user scrolls; fire onChange when
  // it changes. Idempotent if the user's scroll lands on the same mode.
  const onScroll = () => {
    const rail = railRef.current;
    if (!rail) return;
    const center = rail.scrollLeft + rail.clientWidth / 2;
    let best: CaptureMode | null = null;
    let bestDist = Infinity;
    itemRefs.current.forEach((node, id) => {
      const c = node.offsetLeft + node.offsetWidth / 2;
      const d = Math.abs(c - center);
      if (d < bestDist) {
        bestDist = d;
        best = id;
      }
    });
    if (best && best !== lastEmittedRef.current) {
      lastEmittedRef.current = best;
      onChange(best);
    }
  };

  return (
    <div
      ref={railRef}
      role="tablist"
      aria-label="Capture mode"
      onScroll={onScroll}
      style={{
        display: 'flex',
        alignItems: 'center',
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        scrollBehavior: 'smooth',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        background: 'rgba(0, 0, 0, 0.55)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius: 999,
        border: '1px solid rgba(255, 255, 255, 0.08)',
        maxWidth: 360,
        width: '100%',
        height: 48,
        opacity: disabled ? 0.4 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        // Hide scrollbars without removing scroll capability.
        // (scrollbar-width above covers Firefox; WebKit needs the
        // ::-webkit-scrollbar selector which we set via the className
        // below.)
      }}
      className="mode-picker-rail"
    >
      <style>{`
        .mode-picker-rail::-webkit-scrollbar { display: none; }
      `}</style>
      {/* Leading spacer so the first chip can center. */}
      <div style={{ flex: '0 0 50%' }} aria-hidden />
      {visible.map((m) => {
        const active = m.id === mode;
        return (
          <button
            key={m.id}
            ref={(el) => {
              if (el) itemRefs.current.set(m.id, el);
              else itemRefs.current.delete(m.id);
            }}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={disabled}
            onClick={() => onChange(m.id)}
            style={{
              flex: '0 0 auto',
              scrollSnapAlign: 'center',
              scrollSnapStop: 'always',
              minHeight: 36,
              padding: '6px 16px',
              borderRadius: 999,
              border: 'none',
              background: active ? 'var(--accent)' : 'transparent',
              color: active ? '#0b0b0f' : 'rgba(255,255,255,0.6)',
              fontWeight: 600,
              fontSize: 14,
              whiteSpace: 'nowrap',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition:
                'background 160ms ease, color 160ms ease, transform 160ms ease, opacity 160ms ease',
              transform: active ? 'scale(1.0)' : 'scale(0.92)',
            }}
          >
            {m.label}
          </button>
        );
      })}
      {/* Trailing spacer so the last chip can center. */}
      <div style={{ flex: '0 0 50%' }} aria-hidden />
    </div>
  );
}
